// PublicRoute.js
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { tabStorage } from "../../utils/tabStorage";
import { teacherClassPathFromShareTarget } from "../../utils/classShareLink";
import { correctionInFlightRef } from "./BrowserButtons/NavigationGuard";

const PublicRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = tabStorage.getItem("isAuthenticated") === "true";
  const userData = JSON.parse(tabStorage.getItem("userData") || "{}");
  const userRole = userData?.data?.user_role;
  const searchRedirectTarget = (() => {
    try {
      const params = new URLSearchParams(location.search || "");
      return decodeURIComponent(params.get("redirect") || "");
    } catch {
      return "";
    }
  })();
  const hashRedirectTarget = (() => {
    const hash = String(location?.hash || "").trim();
    if (!hash.startsWith("#/")) return "";
    const target = hash.slice(1);
    return target.startsWith("/login") ? "" : target;
  })();

  // A useEffect (not a declarative <Navigate>) so the correctionInFlightRef
  // check below can run after NavigationGuard's own useLayoutEffect for the
  // same commit — see the note on correctionInFlightRef in NavigationGuard.js.
  // Without this, an authenticated visitor's browser-back press that
  // transiently lands here (/login or /) always got redirected to their
  // homepage instead of bounced back to where they actually were, because
  // this redirect used to run unconditionally and raced NavigationGuard's
  // correction.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (correctionInFlightRef.current) return;

    const to =
      userRole === "student"
        ? searchRedirectTarget || hashRedirectTarget || "/studenthomepage"
        : // a teacher who opens a copied class link goes to that class, not the home page
          teacherClassPathFromShareTarget(searchRedirectTarget || hashRedirectTarget) || "/teacherhomepage";
    navigate(to, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userRole, searchRedirectTarget, hashRedirectTarget]);

  if (isAuthenticated) return null;

  return children;
};

export default PublicRoute;
